import { useCallback, useState } from 'react';

import i18n from '@/utils/i18n';
import {
  MerchantOrderStatusEnum,
  MerchantOrderTypeEnum,
  OpsOrderCarrier,
  OpsOrderCarrierService,
  type OpsOrderUpdateInput,
  type OpsOrderContainerUpdateInput,
  type OpsOrderUntrackedItemUpdateInput,
  type OpsOrderItemUpdateInput,
  ContainerTypeSegment,
} from '@/graphql/generatedTypes';
import { isSendCloudCarrier } from '@/components/carrier/carrierLabels';

import type { AddressFormState, FieldErrors, OpsOrderDetail } from './types';
import { isStatusLocked } from './orderFormPolicy';
import {
  isAddressFieldRequired,
  isAddressFieldSupported,
  normalizeName,
  rulesForCarrier,
  validateAddress,
} from './addressRules';
import { useFormDirtyTracking } from '../../hooks/useFormDirtyTracking';
import { initialWarehouseSelection } from './warehouseRouting';

export interface EventItemFormState {
  id: string;
  type_id: number;
  item_location_id: string | null;
  itemLocationName: string;
  boxes: number;
  packingUnit: number;
  amount: number;
  typeName: string;
  imageUrl: string | null;
  hexCode: string | null;
  segment: ContainerTypeSegment | undefined | null;
}

export interface EventUntrackedItemFormState {
  id: string;
  type_id: number;
  item_location_id: string | null;
  itemLocationName: string;
  amount: number;
  typeName: string;
  imageUrl: string | null;
}

/**
 * A CONTAINER_STATION / ASSET ItemType line. Lives in the order's `items`
 * collection (TypeId 0, keyed by item_type_id) — a separate id sequence from
 * the ContainerType-backed containers and untracked items.
 */
export interface EventItemTypeLineFormState {
  id: string;
  item_type_id: number;
  item_location_id: string | null;
  itemLocationName: string;
  amount: number;
  typeName: string;
  imageUrl: string | null;
  hexCode: string | null;
}

export interface EventOrderFormState {
  containers: EventItemFormState[];
  untrackedItems: EventUntrackedItemFormState[];
  itemTypeLines: EventItemTypeLineFormState[];
  status: MerchantOrderStatusEnum;
  deliveryDate: string | null;
  carrierPickupDate: string | null;
  language: string | null;
  warehouseUuid: string | null;
  carrier: OpsOrderCarrier | null;
  carrierService: OpsOrderCarrierService | null;
  address: AddressFormState;
  comment: string;
  opsComment: string;
}

export interface GroupedItemLocation {
  itemLocationId: string | null;
  itemLocationName: string;
  containers: EventItemFormState[];
  untrackedItems: EventUntrackedItemFormState[];
  itemTypeLines: EventItemTypeLineFormState[];
}

function initFormState(order: OpsOrderDetail): EventOrderFormState {
  const ctMap = new Map(
    order.container_types_used_detail.map((c) => [c.id, c]),
  );
  return {
    containers: order.containers.map((item) => {
      const packingUnit = Math.max(1, item.type.packing_unit ?? 1);
      const ctDetail = ctMap.get(item.type.id);
      return {
        id: item.id,
        type_id: item.type.id,
        item_location_id: item.item_location_id ?? null,
        itemLocationName: item.item_location_name ?? i18n.t('Order:unassigned'),
        boxes: item.type.box_count,
        packingUnit,
        amount: item.amount,
        typeName: item.type.name ?? `#${item.type.id}`,
        imageUrl: item.type.image_url ?? null,
        hexCode: ctDetail?.color_hex_code ?? null,
        segment: ctDetail?.segment ?? null,
      };
    }),
    untrackedItems: order.untracked_items.map((item) => ({
      id: item.id,
      type_id: item.type.id,
      item_location_id: item.item_location_id ?? null,
      itemLocationName: item.item_location_name ?? i18n.t('Order:unassigned'),
      amount: item.amount,
      typeName: item.type.name ?? item.name ?? `#${item.type.id}`,
      imageUrl: item.type.image_url ?? null,
    })),
    itemTypeLines: order.items.map((item) => ({
      id: item.id,
      item_type_id: item.item_type.id,
      item_location_id: item.item_location_id ?? null,
      itemLocationName: item.item_location_name ?? i18n.t('Order:unassigned'),
      amount: item.amount,
      typeName: item.item_type.name ?? item.name ?? `#${item.item_type.id}`,
      imageUrl: item.item_type.image_url ?? null,
      hexCode: item.item_type.color_hex_code ?? null,
    })),
    status: order.status,
    deliveryDate: order.delivery_date ?? null,
    carrierPickupDate: order.carrier_pickup_date ?? null,
    language: order.language ?? null,
    warehouseUuid: initialWarehouseSelection(order),
    carrier: order.carrier ?? null,
    carrierService: order.carrier_service ?? null,
    address: initAddressState(order),
    comment: order.comment ?? '',
    opsComment: order.ops_comment ?? '',
  };
}

function initAddressState(order: OpsOrderDetail): AddressFormState {
  const addr = order.delivery_address;
  return {
    name: addr?.name ?? '',
    company: addr?.company ?? '',
    phone_number: addr?.phone_number ?? '',
    email: addr?.email ?? '',
    streetname: addr?.streetname ?? '',
    additional_info: addr?.additional_info ?? '',
    zip: addr?.zip ?? '',
    city: addr?.city ?? '',
    country: addr?.country ?? '',
  };
}

function toSnapshot(form: EventOrderFormState): string {
  return JSON.stringify({
    ...form,
    address: { ...form.address, name: normalizeName(form.address.name) },
  });
}

function addressChanged(a: AddressFormState, b: AddressFormState): boolean {
  return (
    JSON.stringify({ ...a, name: normalizeName(a.name) }) !==
    JSON.stringify({ ...b, name: normalizeName(b.name) })
  );
}

export interface EventItemLocation {
  id: string;
  name: string;
}

function clampAmount(value: number): number {
  return Math.max(0, Number.isFinite(value) ? value : 0);
}

function lineAmountsDiffer(
  current: ReadonlyArray<{ id: string; amount: number }>,
  initial: ReadonlyArray<{ id: string; amount: number }>,
): boolean {
  if (current.length !== initial.length) return true;
  const initialAmounts = new Map(initial.map((line) => [line.id, line.amount]));
  return current.some((line) => initialAmounts.get(line.id) !== line.amount);
}

export function useEventOrderForm(
  order: OpsOrderDetail,
  eventItemLocations?: EventItemLocation[],
) {
  const [errors, setErrors] = useState<FieldErrors>({});
  const clearErrors = useCallback(() => setErrors({}), []);

  const { form, setForm, isDirty, resetToInitial } = useFormDirtyTracking({
    order,
    initFormState,
    toSnapshot,
    onReset: clearErrors,
  });

  const isReadOnly = isStatusLocked(order.status);
  const rules = rulesForCarrier(order, form.carrier);

  // The delivery address is taken from the event and only needs (and allows)
  // editing when the order ships with a parcel carrier through SendCloud, which
  // validates it. With Logistic Partner / Ho'i Ops it stays read-only.
  const isAddressEditable = !isReadOnly && isSendCloudCarrier(form.carrier);

  const setBoxCount = (itemId: string, value: number) => {
    if (isReadOnly) return;
    const boxes = Math.max(0, Number.isFinite(value) ? value : 0);
    setForm((prev) => ({
      ...prev,
      containers: prev.containers.map((item) =>
        item.id === itemId
          ? { ...item, boxes, amount: boxes * item.packingUnit }
          : item,
      ),
    }));
  };

  const removeItem = (itemId: string) => {
    if (isReadOnly) return;
    setForm((prev) => ({
      ...prev,
      containers: prev.containers.filter((item) => item.id !== itemId),
    }));
  };

  const removeUntrackedItem = (itemId: string) => {
    if (isReadOnly) return;
    setForm((prev) => ({
      ...prev,
      untrackedItems: prev.untrackedItems.filter((item) => item.id !== itemId),
    }));
  };

  const removeItemTypeLine = (itemId: string) => {
    if (isReadOnly) return;
    setForm((prev) => ({
      ...prev,
      itemTypeLines: prev.itemTypeLines.filter((item) => item.id !== itemId),
    }));
  };

  const incrementBoxCount = (itemId: string, delta: number) => {
    if (isReadOnly) return;
    setForm((prev) => ({
      ...prev,
      containers: prev.containers.map((item) => {
        if (item.id !== itemId) return item;
        const newBoxes = Math.max(0, item.boxes + delta);
        return {
          ...item,
          boxes: newBoxes,
          amount: newBoxes * item.packingUnit,
        };
      }),
    }));
  };

  const addItem = ({
    typeId,
    typeName,
    imageUrl,
    hexCode,
    boxes,
    packingUnit,
    itemLocationId,
    itemLocationName,
    segment,
  }: {
    typeId: number;
    typeName: string;
    imageUrl: string | null;
    hexCode: string | null;
    boxes: number;
    packingUnit: number;
    itemLocationId: string | null;
    itemLocationName: string;
    segment: ContainerTypeSegment | undefined | null;
  }) => {
    if (isReadOnly) return;
    setForm((prev) => ({
      ...prev,
      containers: [
        ...prev.containers,
        {
          itemLocationName,
          boxes,
          packingUnit,
          typeName,
          imageUrl,
          hexCode,
          segment,
          amount: boxes * packingUnit,
          id: crypto.randomUUID(),
          type_id: typeId,
          item_location_id: itemLocationId,
        },
      ],
    }));
  };

  const addUntrackedItem = ({
    typeId,
    typeName,
    imageUrl,
    itemLocationId,
    itemLocationName,
  }: {
    typeId: number;
    typeName: string;
    imageUrl: string | null;
    itemLocationId: string | null;
    itemLocationName: string;
  }) => {
    if (isReadOnly) return;
    setForm((prev) => ({
      ...prev,
      untrackedItems: [
        ...prev.untrackedItems,
        {
          id: crypto.randomUUID(),
          type_id: typeId,
          item_location_id: itemLocationId,
          itemLocationName,
          amount: 0,
          typeName,
          imageUrl,
        },
      ],
    }));
  };

  const setUntrackedItemAmount = (itemId: string, value: number) => {
    if (isReadOnly) return;
    setForm((prev) => ({
      ...prev,
      untrackedItems: prev.untrackedItems.map((item) =>
        item.id === itemId ? { ...item, amount: clampAmount(value) } : item,
      ),
    }));
  };

  const addItemTypeLine = ({
    itemTypeId,
    typeName,
    imageUrl,
    hexCode,
    amount,
    itemLocationId,
    itemLocationName,
  }: {
    itemTypeId: number;
    typeName: string;
    imageUrl: string | null;
    hexCode: string | null;
    amount: number;
    itemLocationId: string | null;
    itemLocationName: string;
  }) => {
    if (isReadOnly) return;
    setForm((prev) => ({
      ...prev,
      itemTypeLines: [
        ...prev.itemTypeLines,
        {
          id: crypto.randomUUID(),
          item_type_id: itemTypeId,
          item_location_id: itemLocationId,
          itemLocationName,
          amount: clampAmount(amount),
          typeName,
          imageUrl,
          hexCode,
        },
      ],
    }));
  };

  const setItemTypeLineAmount = (itemId: string, value: number) => {
    if (isReadOnly) return;
    setForm((prev) => ({
      ...prev,
      itemTypeLines: prev.itemTypeLines.map((item) =>
        item.id === itemId ? { ...item, amount: clampAmount(value) } : item,
      ),
    }));
  };

  const setStatus = (value: MerchantOrderStatusEnum) => {
    setForm((prev) => ({ ...prev, status: value }));
  };

  const setComment = (value: string) => {
    if (isReadOnly) return;
    setForm((prev) => ({ ...prev, comment: value }));
  };

  const setOpsComment = (value: string) => {
    setForm((prev) => ({ ...prev, opsComment: value }));
  };

  const setDeliveryDate = (value: string | null) => {
    if (isReadOnly) return;
    setForm((prev) => ({ ...prev, deliveryDate: value }));
  };

  const setCarrierPickupDate = (value: string | null) => {
    if (isReadOnly) return;
    setForm((prev) => ({ ...prev, carrierPickupDate: value }));
  };

  const setLanguage = (value: string | null) => {
    if (isReadOnly) return;
    setForm((prev) => ({ ...prev, language: value }));
  };

  const setWarehouseUuid = (value: string | null) => {
    if (isReadOnly) return;
    setForm((prev) => ({ ...prev, warehouseUuid: value }));
  };

  // Event orders are outbound deliveries, so a carrier change always pairs the
  // carrier with HOME_DELIVERY — the service the availability list is filtered
  // on in the sidebar.
  const setCarrier = (value: OpsOrderCarrier) => {
    if (isReadOnly) return;
    setForm((prev) => ({
      ...prev,
      carrier: value,
      carrierService: OpsOrderCarrierService.HomeDelivery,
    }));
    setErrors({});
  };

  const setAddressField = (field: keyof AddressFormState, value: string) => {
    if (!isAddressEditable) return;
    setForm((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`address.${field}`];
      return next;
    });
  };

  const isFieldSupported = (field: keyof AddressFormState): boolean =>
    isAddressFieldSupported(rules, field);
  const isFieldRequired = (field: keyof AddressFormState): boolean =>
    isAddressFieldRequired(rules, field);

  // Only what the user can edit is validated: the address on a parcel-carrier
  // order before it locks (colugo validates it again on approval). A locked
  // order must stay saveable for the fields that remain editable on it.
  const validate = (): FieldErrors | null => {
    const newErrors = isAddressEditable
      ? validateAddress(rules, form.address)
      : {};
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 ? null : newErrors;
  };

  const isBufferOrder = order.type === MerchantOrderTypeEnum.EventBufferOrder;

  const groupedItems: GroupedItemLocation[] = (() => {
    if (isBufferOrder) {
      return [
        {
          itemLocationId: null,
          itemLocationName: i18n.t('Order:bufferOrder'),
          containers: form.containers,
          untrackedItems: form.untrackedItems,
          itemTypeLines: form.itemTypeLines,
        },
      ];
    }
    const groups = new Map<string, GroupedItemLocation>();
    const ensureGroup = (id: string | null, name: string) => {
      const key = id ?? '__unassigned__';
      if (!groups.has(key)) {
        groups.set(key, {
          itemLocationId: id,
          itemLocationName: name,
          containers: [],
          untrackedItems: [],
          itemTypeLines: [],
        });
      }
      return groups.get(key)!;
    };
    for (const loc of eventItemLocations ?? []) {
      ensureGroup(loc.id, loc.name);
    }
    for (const item of form.containers) {
      ensureGroup(item.item_location_id, item.itemLocationName).containers.push(
        item,
      );
    }
    for (const item of form.untrackedItems) {
      ensureGroup(
        item.item_location_id,
        item.itemLocationName,
      ).untrackedItems.push(item);
    }
    for (const item of form.itemTypeLines) {
      ensureGroup(
        item.item_location_id,
        item.itemLocationName,
      ).itemTypeLines.push(item);
    }
    return Array.from(groups.values());
  })();

  // The change guard exists to make ops write a note to the partner whose
  // order they changed or cancelled. A buffer order is Vytal's own stock
  // request and has no partner, so there is nobody the comment could reach.
  // The partner is told about every change to what they ordered: a line
  // added, removed, or with a different amount. Lines are matched by id, so
  // reordering never counts as a change.
  const needsChangeGuard = (() => {
    if (isBufferOrder) return false;
    if (
      form.status === MerchantOrderStatusEnum.Canceled &&
      form.status !== order.status
    )
      return true;
    const initial = initFormState(order);
    return (
      lineAmountsDiffer(form.containers, initial.containers) ||
      lineAmountsDiffer(form.untrackedItems, initial.untrackedItems) ||
      lineAmountsDiffer(form.itemTypeLines, initial.itemTypeLines)
    );
  })();

  const buildUpdateInput = (): OpsOrderUpdateInput => {
    const containers: OpsOrderContainerUpdateInput[] = form.containers.map(
      (item) => ({
        id: item.id,
        type_id: item.type_id,
        amount: item.amount,
        item_location_id: item.item_location_id ?? undefined,
      }),
    );

    const untracked_items: OpsOrderUntrackedItemUpdateInput[] =
      form.untrackedItems.map((item) => ({
        id: item.id,
        type_id: item.type_id,
        amount: item.amount,
        item_location_id: item.item_location_id ?? undefined,
        name: item.typeName,
      }));

    // ItemType lines (container stations / assets) live in their own `items`
    // collection. Sent only on the order types whose colugo allowlist accepts
    // them — on any other type there is no picker for them, and omitting the
    // key makes colugo clone whatever rows are already there rather than read
    // an empty list as "delete them all".
    const orderTypeAllowsItemTypeLines =
      order.type === MerchantOrderTypeEnum.EventBufferOrder ||
      order.type === MerchantOrderTypeEnum.EventAccountOrder;
    const items: OpsOrderItemUpdateInput[] | undefined =
      orderTypeAllowsItemTypeLines
        ? form.itemTypeLines.map((item) => ({
            id: item.id,
            item_type_id: item.item_type_id,
            amount: item.amount,
            item_location_id: item.item_location_id ?? undefined,
          }))
        : undefined;

    return {
      containers,
      untracked_items,
      ...(items != null && { items }),
      comment: form.comment,
      ops_comment: form.opsComment,
      ...(form.status !== order.status && { status: form.status }),
      ...(form.deliveryDate !== (order.delivery_date ?? null) && {
        delivery_date: form.deliveryDate,
      }),
      ...(form.carrierPickupDate !== (order.carrier_pickup_date ?? null) && {
        carrier_pickup_date: form.carrierPickupDate,
      }),
      ...(form.language !== (order.language ?? null) && {
        language: form.language,
      }),
      // null switches the order back to its automatic warehouse.
      ...(form.warehouseUuid !== initialWarehouseSelection(order) && {
        warehouse_uuid: form.warehouseUuid,
      }),
      // Sent only when changed, like every other order-level field.
      ...(form.carrier !== (order.carrier ?? null) && {
        carrier: form.carrier,
        carrier_service: form.carrierService,
      }),
      // Likewise for the address: colugo writes a new address row for every
      // update that carries one, so only an actual edit sends it.
      ...(addressChanged(form.address, initAddressState(order)) && {
        delivery_address: {
          name: normalizeName(form.address.name),
          company: form.address.company,
          phone_number: form.address.phone_number,
          email: form.address.email,
          streetname: form.address.streetname,
          additional_info: form.address.additional_info,
          zip: form.address.zip,
          city: form.address.city,
          country: form.address.country,
        },
      }),
    };
  };

  return {
    form,
    groupedItems,
    setBoxCount,
    incrementBoxCount,
    addItem,
    removeItem,
    addUntrackedItem,
    setUntrackedItemAmount,
    removeUntrackedItem,
    addItemTypeLine,
    setItemTypeLineAmount,
    removeItemTypeLine,
    setComment,
    setOpsComment,
    setStatus,
    setDeliveryDate,
    setCarrierPickupDate,
    setLanguage,
    setWarehouseUuid,
    setCarrier,
    setAddressField,
    isFieldSupported,
    isFieldRequired,
    validate,
    errors,
    isAddressEditable,
    isReadOnly,
    isDirty,
    needsChangeGuard,
    resetToInitial,
    buildUpdateInput,
  };
}
